import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AdditionalSettingsModal } from '../additional-settings-modal';
import { BalanceLoader } from '../balance-loader/balance-loader';
import { EmptyWalletSection } from '../empty-wallet-section/empty-wallet-section';

import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import GuysLoader from 'shared/components/ui/Loaders/GuysLoader/GuysLoader';
import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import Select from 'shared/components/ui/Select/Select';
import { BaseModal } from 'shared/components/ui/base-modal/base-modal';
import BetInput from 'shared/components/ui/bet-input/bet-input';
import { Switch } from 'shared/components/ui/switch/switch';
import { allowanceMessages, creatingMessages } from 'shared/constants/text-loaders';
import { useAccount } from 'shared/hooks/use-web3';
import { useGlobalStore } from 'shared/store/globalStore';
import { prettyTx } from 'shared/utils/pretty-tx';
import { formatNumber } from 'shared/web3/utils';
import { CurrencyType } from 'shared/web3/utils/getCurrencyInfo';

import SettingsIcon from 'assets/settings.svg?react';
import SwordsIcon from 'assets/svg/swords.svg?react';

import { getOptionsWithBalances, getSelectedOptionWithBalance } from './create-game-section.utils';
import { useCreateGameSection } from './useCreateGameSection';

interface ICreateGameSectionProps {
  approveAllowanceHandler: (fullAllowance: boolean, bet: number) => void;
  startGameHandler: (bet: number, wins: number, playerB: string, currency?: CurrencyType) => void;
}

export const CreateGameSection: FC<ICreateGameSectionProps> = ({
  approveAllowanceHandler,
  startGameHandler,
}) => {
  const { t } = useTranslation();

  const {
    bet,
    selectedCurrency,
    selectedBalance,
    isApproving,
    getAuthorizeDisable,
    fullAllowance,
    betIsValid,
    currentAllowance,
    isFetching,
    isStarting,
    txHash,
    betError,
    currencyForSelect,
    isModalOpened,
    balances,
    isLoadingBalances,
    handleSelectCurrency,
    handleSetBetInput,
    modalOpenHandler,
    handleSetBet,
    handleSwitchChange,
    handleStartGame,
    handleApprove,
  } = useCreateGameSection({
    approveAllowanceHandler,
    startGameHandler,
  });
  const [searchParams] = useSearchParams();
  const { address } = useAccount();

  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);

  const optionsWithBalances = useMemo(
    () => getOptionsWithBalances(currencyForSelect, balances),
    [currencyForSelect, balances],
  );

  const selectedOptionWithBalance = useMemo(() => {
    if (!selectedCurrency) return null;
    return getSelectedOptionWithBalance({
      value: selectedCurrency.value,
      symbol: selectedCurrency.symbol,
    });
  }, [selectedCurrency]);

  const isStartGameDisabled = useMemo(
    () =>
      !bet ||
      !betIsValid ||
      Number(currentAllowance) < +bet ||
      isApproving ||
      isFetching ||
      isStarting ||
      (selectedBalance ?? 0) < +bet,
    [isApproving, isFetching, betIsValid, currentAllowance, bet, selectedBalance, isStarting],
  );

  const isLoading = useMemo(() => isLoadingBalances || isFetching, [isLoadingBalances, isFetching]);

  const shouldShowWalletWarning = useMemo(
    () => (selectedBalance <= 0 || +bet > selectedBalance) && !isLoading,
    [selectedBalance, bet, isLoading],
  );

  const shouldShowActionButtons = useMemo(
    () => selectedBalance > 0 && !isLoading && selectedBalance >= +bet,
    [selectedBalance, isLoading, bet],
  );

  const screenAddress = useMemo(
    () => prettyTx({ address: searchParams.get('invite'), leaderboardMap }),
    [searchParams.get('invite'), leaderboardMap],
  );

  return (
    <div className="relative z-10 mb-5 sm:mb-0">
      <div className="gradient-title text-[20px] font-bold lg:text-[52px]">
        {t('modals.setBet.title')}
      </div>

      <div className="relative mt-6 w-full rounded-3xl border border-border bg-[linear-gradient(0deg,#080540,#080540),radial-gradient(50%_50%_at_50%_50%,rgba(0,11,255,0.3)_0%,rgba(1,0,29,0.3)_100%)] lg:min-w-[30rem] lg:border-[3px] xl:min-w-[36.125rem] xl:max-w-[36.125rem]">
        {isLoading || !selectedCurrency ? (
          <BalanceLoader />
        ) : (
          <>
            <AnimatedTransition show={isApproving || isStarting} className="z-50">
              <GuysLoader
                hash={txHash}
                messages={isApproving ? allowanceMessages : creatingMessages}
                className="absolute left-0 top-0 z-50 h-full w-full rounded-3xl bg-[#000014D9]/85"
              />
            </AnimatedTransition>

            <div className="mb-6 rounded-t-3xl border-b-2 border-border bg-[linear-gradient(180deg,_#080540_0%,_#030220_100%)] px-6 pt-6">
              <div className="mb-6 flex items-center justify-between">
                <p className="gradient-title text-[24px] font-bold text-white">
                  {t('modals.setBet.select')}
                </p>

                <button
                  className="rounded-[10px] border-2 border-indigo bg-border p-[3px]"
                  onClick={modalOpenHandler}
                  data-testid="settings-button"
                >
                  <SettingsIcon className="h-[1.875rem] w-[1.875rem]" />
                </button>
              </div>

              <div className="mb-4 flex w-full items-start justify-between border-b border-border pb-2">
                <div className="mr-2.5 flex w-[70%] flex-col sm:mr-3 sm:flex-grow">
                  <BetInput
                    value={bet.toString()}
                    valueHandler={handleSetBetInput}
                    type="number"
                    className="sm:flex-grow"
                    balance={selectedBalance ?? 0}
                    disabled={isApproving || isLoading}
                    betError={betError}
                    selectedCurrency={selectedCurrency}
                  />
                </div>

                <Select
                  className="flex-grow"
                  options={optionsWithBalances}
                  selectedOption={selectedOptionWithBalance}
                  onSelect={handleSelectCurrency}
                  disabled={isApproving || isLoading}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between pb-5">
                <p className="text-base font-semibold text-simple-text">
                  {t('modals.setBet.popular')}
                </p>
                <div className="item-center flex justify-end gap-3">
                  {selectedCurrency?.bet?.map((betValue) => (
                    <button
                      key={betValue}
                      className="!disabled:bg-[linear-gradient(91deg,_#05055B_-6.69%,_#151552_106.59%)] rounded-xl border-2 border-indigo p-3 text-base font-medium leading-5 text-light-grape transition-transform hover:opacity-90 active:scale-95 disabled:cursor-not-allowed disabled:bg-gray lg:min-w-28"
                      onClick={() => handleSetBet(betValue)}
                      disabled={isApproving || isLoading}
                    >
                      {formatNumber(+betValue)} {selectedCurrency.symbol}
                    </button>
                  ))}
                </div>
              </div>
              {searchParams.get('invite') && address !== searchParams.get('invite') && (
                <div className="mb-5 rounded-xl bg-gradient-to-r from-[#3BC587] to-[#399197] p-[1px]">
                  <div className="flex items-center gap-2 rounded-xl bg-[linear-gradient(91deg,_#05055B_-6.69%,_#074540_106.59%)] px-3 py-2.5">
                    <SwordsIcon className="h-[1.25rem] w-[1.25rem] text-[#3CD2C7]/75" />
                    <p className="text-xs text-[#3CD2C7]/75">
                      {t('createGameSection.invited', {
                        player: screenAddress,
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {shouldShowActionButtons && (
              <div className="px-6 pb-6">
                <AnimatedTransition show={!getAuthorizeDisable}>
                  <div className="flex items-center gap-2">
                    <p className="mr-3 border-r border-border pr-3 text-[14px] text-light-gray">
                      {t('modals.setBet.plan')}
                    </p>
                    <Switch
                      className="ml-2"
                      onCheckedChange={handleSwitchChange}
                      checked={fullAllowance}
                    />
                  </div>
                </AnimatedTransition>

                <div className="z-0 mt-4 grid grid-cols-2 gap-4">
                  <PrimaryButton
                    text="common.authorize"
                    disabled={getAuthorizeDisable}
                    onClick={handleApprove}
                  />
                  <PrimaryButton
                    text="common.startGame"
                    disabled={isStartGameDisabled}
                    onClick={handleStartGame}
                  />
                </div>
              </div>
            )}
            {shouldShowWalletWarning && <EmptyWalletSection />}
          </>
        )}
      </div>

      {isModalOpened && (
        <BaseModal>
          <AdditionalSettingsModal onClose={modalOpenHandler} />
        </BaseModal>
      )}
    </div>
  );
};
